import React, { useEffect, useRef, useState } from "react";
import StaticTemplatePreview from "@/components/StaticTemplatePreview";

/**
 * HeroVolantinoFlip — un'unica A5 che ruota tra fronte e retro ogni ~3.5s.
 *
 * Sostituisce la vista side-by-side che l'utente trovava confusa.
 * Implementazione: CSS 3D flip su un wrapper `preserve-3d`. Le due facciate
 * sono assolute-stack, una con `rotateY(0)` e l'altra con `rotateY(180)`.
 * Il container ruota di 180° ad ogni tick e si alterna fra le due
 * `backface-hidden` per non vedere il retro a specchio.
 *
 * `active` (default true): se false ferma l'animazione (es. quando il chip
 * non è il showcase corrente).
 */
export default function HeroVolantinoFlip({ active = true }) {
  const [flipped, setFlipped] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => setFlipped((f) => !f), 3500);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [active]);

  const sharedProps = {
    template: "volantino_stima",
    layout: "luxury",
    color: "verde_brera",
    property: { title: "Stima", city: "La tua zona", photos: [] },
    agency: {
      name: "Studio Demo Immobiliare",
      agent_name: "Il tuo nome qui",
      phone: "+39 333 000 0000",
      email: "info@tuaagenzia.it",
      website: "tuaagenzia.it",
      area_short: "La tua zona",
      license: "LIC. ABI",
      logo_url: null,
      agent_photo_url: null,
      estimate_url_short: "tuaagenzia.it/stima",
      estimate_url_full: "https://tuaagenzia.it/stima",
      qr_dataurl: null,
    },
  };

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#F1F4ED] to-[#E5E7E0] p-3 sm:p-4"
      data-testid="hero-volantino-flip"
    >
      {/* perspective sul parent così il 3D è più naturale */}
      <div style={{ perspective: "1400px" }}>
        <div
          className="relative"
          style={{
            aspectRatio: "540 / 766",
            transformStyle: "preserve-3d",
            transition: "transform 900ms cubic-bezier(0.6, 0.05, 0.35, 0.95)",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
            willChange: "transform",
          }}
        >
          {/* FRONTE */}
          <div
            className="absolute inset-0"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
            }}
          >
            <StaticTemplatePreview {...sharedProps} side="front" />
          </div>
          {/* RETRO */}
          <div
            className="absolute inset-0"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <StaticTemplatePreview {...sharedProps} side="back" />
          </div>
        </div>
      </div>

      {/* Dot indicator — quale facciata è visibile ora */}
      <div className="flex items-center justify-center gap-1.5 mt-3" aria-hidden="true">
        <span
          className={`block w-1.5 h-1.5 rounded-full transition-colors ${
            !flipped ? "bg-[#7C8B6E]" : "bg-[#C8C5BD]"
          }`}
        />
        <span
          className={`block w-1.5 h-1.5 rounded-full transition-colors ${
            flipped ? "bg-[#7C8B6E]" : "bg-[#C8C5BD]"
          }`}
        />
        <span className="text-[9.5px] uppercase tracking-[0.18em] font-semibold text-[#9A968B] ml-2">
          {flipped ? "Retro" : "Fronte"}
        </span>
      </div>
    </div>
  );
}
